import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Divider,
} from '@mui/material';
// Date/time handling without external picker
import { Close as CloseIcon } from '@mui/icons-material';
import { useCreateItemMutation, useUpdatePropertyMutation } from '../store/docApi';

const initialFormData = {
  title: '',
  description: '',
  startDateTime: new Date(),
  endDateTime: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours later
  location: {
    type: 'physical',
    name: '',
    address: '',
    url: ''
  },
  maxCapacity: 0,
  price: 0,
  currency: 'USD',
  imageUrl: '',
  category: 'workshop',
  skillLevel: 'all'
};

export default function EventForm({ event, onClose }) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [createEvent] = useCreateItemMutation();
  const [updateProperty] = useUpdatePropertyMutation();

  useEffect(() => {
    if (event) {
      // Convert string dates to Date objects for the form
      const eventWithDateObjects = {
        ...event,
        startDateTime: new Date(event.startDateTime),
        endDateTime: new Date(event.endDateTime)
      };
      setFormData(eventWithDateObjects);
    } else {
      setFormData(initialFormData);
    }
  }, [event]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.startDateTime) {
      newErrors.startDateTime = 'Start date is required';
    }
    
    if (!formData.endDateTime) {
      newErrors.endDateTime = 'End date is required';
    } else if (formData.startDateTime && formData.endDateTime <= formData.startDateTime) {
      newErrors.endDateTime = 'End date must be after start date';
    }
    
    if (formData.location.type === 'physical' && !formData.location.address) {
      newErrors.location = { ...newErrors.location, address: 'Address is required for physical events' };
    }
    
    if (formData.location.type === 'online' && !formData.location.url) {
      newErrors.location = { ...newErrors.location, url: 'URL is required for online events' };
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        [name]: value
      }
    }));
  };

  const handleLocationTypeChange = (e) => {
    const type = e.target.value;
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        type,
        // Reset URL when switching to physical and vice versa
        ...(type === 'physical' ? { url: '' } : { address: '' })
      }
    }));
  };

  const handleDateTimeChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: new Date(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const eventData = {
        label: 'event',
        ...formData,
        // Ensure dates are ISO strings for the API
        startDateTime: formData.startDateTime.toISOString(),
        endDateTime: formData.endDateTime.toISOString()
      };

      if (event) {
        // Update existing event
        const updates = Object.entries(eventData).map(([key, value]) =>
          updateProperty({
            label: 'event',
            id: event.id,
            name: key,
            value
          }).unwrap()
        );
        await Promise.all(updates);
      } else {
        // Create new event
        console.log(eventData);
        await createEvent({label: 'event', data: eventData}).unwrap();
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save event:', error);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {event ? 'Edit Event' : 'Create New Event'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Event Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={!!errors.title}
            helperText={errors.title}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
            margin="normal"
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Date & Time
            </Typography>
            <Divider />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  type="datetime-local"
                  name="startDateTime"
                  value={formData.startDateTime ? formData.startDateTime.toISOString().slice(0, 16) : ''}
                  onChange={handleDateTimeChange}
                  error={!!errors.startDateTime}
                  helperText={errors.startDateTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  name="endDateTime"
                  value={formData.endDateTime ? formData.endDateTime.toISOString().slice(0, 16) : ''}
                  onChange={handleDateTimeChange}
                  error={!!errors.endDateTime}
                  helperText={errors.endDateTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formData.startDateTime ? formData.startDateTime.toISOString().slice(0, 16) : ''
                  }}
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Location
            </Typography>
            <Divider />
            <FormControl fullWidth margin="normal">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.location.type}
                onChange={handleLocationTypeChange}
                label="Event Type"
              >
                <MenuItem value="physical">In-Person</MenuItem>
                <MenuItem value="online">Online</MenuItem>
              </Select>
            </FormControl>

            {formData.location.type === 'physical' ? (
              <>
                <TextField
                  fullWidth
                  label="Venue Name"
                  name="name"
                  value={formData.location.name}
                  onChange={handleLocationChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.location.address}
                  onChange={handleLocationChange}
                  error={!!errors.location?.address}
                  helperText={errors.location?.address}
                  margin="normal"
                  required
                />
              </>
            ) : (
              <TextField
                fullWidth
                label="Meeting URL"
                name="url"
                value={formData.location.url}
                onChange={handleLocationChange}
                error={!!errors.location?.url}
                helperText={errors.location?.url || 'e.g., Zoom, Google Meet, etc.'}
                margin="normal"
                required
              />
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ mt: { xs: 0, md: 2 } }}>
            <Typography variant="subtitle1" gutterBottom>
              Event Details
            </Typography>
            <Divider />

            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
              >
                <MenuItem value="workshop">Workshop</MenuItem>
                <MenuItem value="knit-along">Knit-Along</MenuItem>
                <MenuItem value="social">Social Gathering</MenuItem>
                <MenuItem value="class">Class</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Skill Level</InputLabel>
              <Select
                name="skillLevel"
                value={formData.skillLevel}
                onChange={handleChange}
                label="Skill Level"
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Maximum Capacity"
              name="maxCapacity"
              type="number"
              value={formData.maxCapacity}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 0 }}
              helperText="0 for unlimited"
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      $
                    </Typography>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  label="Currency"
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="CAD">CAD</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="JPY">JPY</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              label="Image URL"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              margin="normal"
              helperText="URL of the event banner image"
            />

            {formData.imageUrl && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Image Preview:
                </Typography>
                <Box
                  component="img"
                  src={formData.imageUrl}
                  alt="Event preview"
                  sx={{ 
                    width: '100%', 
                    maxHeight: 150, 
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid #eee'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {event ? 'Update Event' : 'Create Event'}
        </Button>
      </Box>
    </Box>
  );
}
